import React from 'react';
import { Paper, Typography, Box, Grid, Divider } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import CarCrashIcon from '@mui/icons-material/CarCrash';

const AutoPlusReview = () => {
  // 模拟 Auto+ 数据
  const autoPlusData = {
    insuranceHistory: [
      {
        company: 'Intact Insurance',
        policyNumber: 'IN-123456',
        startDate: '2022-01-15',
        endDate: '2023-01-15',
        drivers: [
          {
            name: 'John Smith',
            licenseNumber: 'S1234-56789-12345',
            primaryDriver: true
          },
          {
            name: 'Mary Smith',
            licenseNumber: 'S9876-54321-98765',
            primaryDriver: false
          }
        ]
      },
      {
        company: 'Aviva Canada',
        policyNumber: 'AV-789012',
        startDate: '2020-01-15',
        endDate: '2022-01-15',
        drivers: [
          {
            name: 'John Smith',
            licenseNumber: 'S1234-56789-12345',
            primaryDriver: true
          }
        ]
      }
    ],
    claims: [
      {
        date: '2023-06-15',
        type: 'Collision',
        description: 'Rear-end collision with another vehicle',
        atFault: true,
        cost: '$5,000',
        insuranceCompany: 'Intact Insurance',
        status: 'Closed'
      },
      {
        date: '2023-02-10',
        type: 'DCPD',
        description: 'Vehicle was damaged in parking lot by identified vehicle',
        atFault: false,
        cost: '$3,800',
        insuranceCompany: 'Intact Insurance',
        status: 'Closed'
      },
      {
        date: '2022-11-15',
        type: 'Hit and Run',
        description: 'Vehicle was damaged by unidentified vehicle while parked',
        atFault: false,
        cost: '$2,500',
        insuranceCompany: 'Intact Insurance',
        status: 'Closed'
      },
      {
        date: '2022-08-22',
        type: 'Comprehensive',
        description: 'Windshield damaged by vandalism',
        atFault: false,
        cost: '$800',
        insuranceCompany: 'Intact Insurance',
        status: 'Closed'
      }
    ]
  };

  const getClaimTypeColor = (type, atFault) => {
    switch (type) {
      case 'Collision':
        return atFault ? '#fff3e0' : '#f5f5f5'; // 碰撞事故，根据责任判定颜色
      case 'DCPD':
        return '#e8f5e9'; // DCPD类型使用绿色系
      case 'Hit and Run':
        return '#fff8e1'; // Hit and Run 使用黄色系
      case 'Comprehensive':
        return '#e3f2fd'; // 综合险类型使用蓝色系
      default:
        return '#f5f5f5';
    }
  };

  const getClaimTypeBorderColor = (type, atFault) => {
    switch (type) {
      case 'Collision':
        return atFault ? '#ffe0b2' : '#e0e0e0';
      case 'DCPD':
        return '#c8e6c9';
      case 'Hit and Run':
        return '#ffe082';
      case 'Comprehensive':
        return '#bbdefb';
      default:
        return '#e0e0e0';
    }
  };

  const InsuranceHistoryCard = ({ history }) => (
    <Box sx={{ 
      p: 2, 
      mb: 2, 
      bgcolor: '#f5f5f5', 
      borderRadius: 1,
      border: '1px solid #e0e0e0'
    }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: '#1565c0',
            fontWeight: 500 
          }}>
            <TimelineIcon sx={{ mr: 1 }} />
            {history.company}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Policy Number: {history.policyNumber}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Period: {history.startDate} to {history.endDate}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Listed Drivers:
          </Typography>
          {history.drivers.map((driver, index) => (
            <Box key={index} sx={{ ml: 2, mb: 1 }}>
              <Typography variant="body2">
                {driver.name} {driver.primaryDriver && '(Principal Driver)'} 
              </Typography>
              <Typography variant="body2" color="text.secondary">
                License: {driver.licenseNumber}
              </Typography>
            </Box>
          ))}
        </Grid>
      </Grid>
    </Box>
  );

  const ClaimCard = ({ claim }) => (
    <Box sx={{ 
      p: 2, 
      mb: 2, 
      bgcolor: getClaimTypeColor(claim.type, claim.atFault),
      borderRadius: 1,
      border: `1px solid ${getClaimTypeBorderColor(claim.type, claim.atFault)}`
    }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: claim.type === 'Collision' && claim.atFault ? 'warning.dark' : 'text.primary'
          }}>
            <CarCrashIcon sx={{ mr: 1, fontSize: 20 }} />
            {claim.type}
            {claim.type === 'Collision' && (
              <Typography component="span" sx={{ ml: 1, color: claim.atFault ? 'warning.dark' : 'success.main' }}>
                ({claim.atFault ? 'At Fault' : 'Not At Fault'})
              </Typography>
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Date: {claim.date}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Cost: {claim.cost}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2">
            {claim.description}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            Insurance Company: {claim.insuranceCompany}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, color: '#1565c0' }}>
        Auto+ Report Summary
      </Typography>

      <Grid container spacing={3}>
        {/* Insurance History */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Insurance History
          </Typography>
          {autoPlusData.insuranceHistory.map((history, index) => (
            <InsuranceHistoryCard key={index} history={history} />
          ))}
          <Divider sx={{ my: 3 }} />
        </Grid>

        {/* Claims History */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Claims History
          </Typography>
          {autoPlusData.claims.map((claim, index) => (
            <ClaimCard key={index} claim={claim} />
          ))}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AutoPlusReview; 